/** The execution frequencies for the job */
export var ExecutionFrequencies;
(function (ExecutionFrequencies) {
    ExecutionFrequencies[ExecutionFrequencies["EverySession"] = 0] = "EverySession";
    ExecutionFrequencies[ExecutionFrequencies["EveryWeek"] = 604800] = "EveryWeek";
})(ExecutionFrequencies || (ExecutionFrequencies = {}));
/** Generates a namesapced topic for use by the application */
export const generateTopic = (name) => `bp:topic:${name}`;
// Implementation of the Job interface
export class JobContext {
    /**
     * Create your job instance
     */
    constructor({ topic, schedule, durable, jobKey, expiration, }) {
        this.topic = topic;
        this.schedule = schedule;
        this.durable = durable;
        this.jobKey = jobKey;
        this.expiration = expiration;
    }
    /**
     * Retrieves the topic for this job instance
     * @returns Topic for job
     */
    getTopic() {
        return this.topic;
    }
    /**
     * Retrieves the unique key for this job instance
     * @returns Unique key for job
     */
    getJobKey() {
        return this.jobKey;
    }
    /**
     * Retrieves the job criteria object for this given instance
     * @returns Job criteria object
     */
    getCriteria() {
        const criteria = {
            jobKey: this.jobKey,
            topic: this.topic,
            schedule: this.schedule,
            timesSeen: this.timesSeen,
            durable: this.durable,
            // Convert the complex dates to second based epochs
            lastExecution: Math.floor((this.lastExecution || new Date()).valueOf() / 1000),
        };
        if (this.expiration) {
            criteria.expiration = Math.floor(this.expiration.valueOf() / 1000);
        }
        return criteria;
    }
    /**
     * Hydrates a new job object with provided criteria
     * @param param A job criteria object
     * @returns New job containing defined criteria
     */
    hydrateFromCriteria({ jobKey, topic, expiration, schedule, lastExecution, timesSeen, durable, }) {
        this.topic = topic;
        this.jobKey = jobKey;
        this.schedule = schedule;
        this.durable = durable;
        this.timesSeen = timesSeen;
        this.lastExecution = new Date(lastExecution * 1000);
        if (expiration) {
            this.expiration = new Date(expiration * 1000);
        }
        return this;
    }
    /**
     * Determine whether job should persist in cookie and stay in job queue
     * @returns Whether or not job is durable
     */
    isDurable() {
        return this.durable;
    }
    /**
     * Detect if the job already exists in the job queue
     * @param job Job to compare against
     * @returns Whether or not job is a duplicate
     */
    isDuplicate(job) {
        return this.getJobKey() === job.getJobKey();
    }
    /**
     * Determine whether the job is able to be played or not.
     * @returns Whether or not the job is playable
     */
    isPlayable() {
        // Grab the current schedule
        const currentSchedule = this.schedule[0];
        // Special handling incase we're inside of a session based frequency
        if (currentSchedule.executionFrequency === ExecutionFrequencies.EverySession) {
            if (currentSchedule.replays <= this.timesSeen) {
                // Once we've gone through the schedule, and it's replays, reset the times seen
                this.timesSeen = 1;
                // Move onto the next part of the schedule
                if (this.schedule.length > 1) {
                    this.schedule.shift();
                }
            }
            return true;
        }
        // Grab the next cycle reset timestamp
        const cycleResetEpoch = this.lastExecution.valueOf() + (currentSchedule.executionFrequency * 1000);
        // Check to see if it's time to move one
        if (cycleResetEpoch < new Date().valueOf()) {
            // Once we've gone through the schedule, and it's replays, reset the times seen
            this.timesSeen = 1;
            // Move onto the next part of the schedule
            if (this.schedule.length > 1) {
                this.schedule.shift();
            }
        }
        // Update replay amount to value stored at first index of replays array
        const currentReplays = this.schedule[0].replays;
        // Make sure we've not seen the job too much
        if (currentReplays < this.timesSeen) {
            return false;
        }
        return true;
    }
    /**
     * Increment the times we've seen the job
     */
    addPlay() {
        if (typeof this.timesSeen !== 'number') {
            this.timesSeen = 0;
        }
        // Update job's last execution to this moment in time
        this.lastExecution = new Date();
        // Increment the amount of times this job has been seen by 1
        this.timesSeen += 1;
    }
}
